import { useMemo } from 'react';

import { useBundles } from './use-bundles';
// From the leaf constants module, not './bundles-row' -- avoids pulling in BundleCard's
// styled-components tree. See bundles-row.constants.ts.
import { MAX_BUNDLES_DISPLAYED } from './bundles-row.constants';

/**
 * Product IDs currently shown in a bundle, for callers (e.g. specials-carousel.jsx) that need to
 * exclude them from their own listing -- but only when BundlesRow will actually render in their
 * place (see isCarouselSectionEligibleForRender's BUNDLES case).
 *
 * Built from the capped set BundlesRow actually renders, not the uncapped bundleMemberships, so a
 * product whose bundle got cut by the display cap isn't hidden with nothing in its place. Matches
 * by both productId and enterpriseProductId, same as isBundledProduct in specials-carousel.jsx.
 *
 * Fold the returned `loading` into your own loading state, or bundle products will render then
 * disappear once this resolves.
 */
export function useBundleProductIds({ skip }: { skip?: boolean } = {}): {
  bundleProductIds: Set<string>;
  loading: boolean;
} {
  const { bundles, loading } = useBundles({ skip });

  const bundleProductIds = useMemo(() => {
    const displayedBundles = bundles.slice(0, MAX_BUNDLES_DISPLAYED);
    const ids = displayedBundles.flatMap((bundle) =>
      bundle.items.flatMap((item) => [item.productId, item.product.id, item.product.enterpriseProductId])
    );
    return new Set(ids.filter((id): id is string => Boolean(id)));
  }, [bundles]);

  return { bundleProductIds, loading };
}
